import React, { useEffect, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { ClipLoader } from "react-spinners";
import { Box, Typography } from "@mui/material";
import { BarChart as BarChartIcon } from "@mui/icons-material";
import getDestinationIcon from "helpers/destinations/getDestinationIcon";

const ChartComponent = (props) => {
    const [renderData, setRenderData] = useState([]);
    const [barKeys, setBarKeys] = useState(["Total"]);
    const [colors, setColors] = useState([
        "#0066FF",
        "#8B5CF6",

        "#F66700",
        "#FFA800",
        "#FF0000",
        "#10B981",

        "#06B6D4",
        "#EC4899",
        "#F59E0B",
        "#EF4444",
    ]);
    const [loader, setLoader] = useState(false);

    useEffect(() => {
        setLoader(true);
    }, []);

    useEffect(() => {
        if (props?.colors) {
            setColors(props?.colors);
        }
        if (props?.loader !== undefined && props?.loader === true) {
            setLoader(true);
        }
    }, props);
    useEffect(() => {
        let RenderChartData =
            props.chartData &&
            props.chartData &&
            props.chartData.length > 0 &&
            props.chartData;
        setRenderData(RenderChartData);
        if (props.chartData && props.chartData && props.chartData.length > 0) {
            setLoader(true);
            if (
                [...Object.keys(RenderChartData[0])].filter(
                    (x) => x !== "name" && x !== "Total",
                ).length > 0
            ) {
                // Get all keys except name, Total, and errors
                let availableKeys = [...Object.keys(RenderChartData[0])].filter(
                    (x) => x !== "name" && x !== "Total" && x !== "errors",
                );

                // Only include Total if there are multiple data sets
                let _keys = availableKeys.length >= 1 ? [] : ["Total"];

                availableKeys.forEach((key) => {
                    if (_keys.find((x) => x === key) === undefined) {
                        _keys.push(key);
                    }
                });
                setBarKeys(_keys);
                setTimeout(() => {
                    setLoader(false);
                }, 1500);
            } else {
                setTimeout(() => {
                    setLoader(false);
                }, 1500);
            }
        } else {
            setTimeout(() => {
                setLoader(false);
            }, 1500);
        }
    }, [props?.chartData, props.selectedStatus]);

    const getDestCode = (key) => {
        if (key.split("|").length > 1) {
            return key.split("|")[1] !== "+" ? key.split("|")[1] : "";
        } else {
            return key;
        }
    };

    const getDestName = (key) => {
        if (key.split("|").length > 1) {
            return key.split("|")[0];
        } else {
            return key;
        }
    };

    const getDestinationIconHandler = (key) => {
        let code = getDestCode(key);
        if (code !== "") {
            return getDestinationIcon(code);
        } else {
            return "";
        }
    };

    const destinationList =
        props?.destinationList !== undefined ? props?.destinationList : [];

    const renderLegend = (props) => {
        const { payload } = props;

        const getColorForDestination = (entry, index) => {
            if (entry.value === "All Errors") {
                return "red";
            }
            return colors[index];
        };

        return destinationList.length > 0 ? (
            <div className="chart-legend-list">
                {payload &&
                    payload.length > 0 &&
                    payload.map((entry, index) => {
                        return (
                            <div data-key={entry.value} key={`item-${index}`}>
                                <span
                                    data-color={getColorForDestination(
                                        entry,
                                        index,
                                    )}
                                    className="square"
                                ></span>
                                {getDestinationIconHandler(entry.value) !==
                                    "" && (
                                    <span className="dest-icon">
                                        <img
                                            width={16}
                                            src={getDestinationIconHandler(
                                                entry.value,
                                            )}
                                        />
                                    </span>
                                )}
                                <span className="title">
                                    {getDestName(entry.value)}
                                </span>
                            </div>
                        );
                    })}
            </div>
        ) : (
            <div className="chart-legend-list">
                {payload &&
                    payload.length > 0 &&
                    payload.map((entry, index) => {
                        return (
                            <div data-key={entry.value} key={`item-${index}`}>
                                <span
                                    data-color={getColorForDestination(
                                        entry,
                                        index,
                                    )}
                                    className="square"
                                ></span>
                                <span className="title">{entry.value}</span>
                            </div>
                        );
                    })}
            </div>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-chart-tooltip">
                    {payload[0]?.payload &&
                        Object.keys(payload[0]?.payload)
                            .filter((key) =>
                                props.selectedStatus === "success"
                                    ? key !== "errors"
                                    : key === "errors",
                            )
                            .map((key, i) =>
                                key !== "name" ? (
                                    <p className="label" key={key}>{`${
                                        key === "errors"
                                            ? `${payload[0]?.payload.name} All Errors`
                                            : getDestName(key)
                                    } : ${payload[0]?.payload[key]}`}</p>
                                ) : (
                                    <p className="label" key={key}>
                                        {payload[0]?.payload[key]}
                                    </p>
                                ),
                            )}
                </div>
            );
        }

        return null;
    };
    // Create stacked data with errors at the top
    const createStackedData = () => {
        if (!renderData || renderData.length === 0) return [];

        return renderData.map((item) => {
            const stackedItem = { name: item.name };

            // Add regular keys first (bottom of stack)
            barKeys.forEach((key) => {
                if (
                    key !== "errors" &&
                    key !== "Total" &&
                    item[key] !== undefined
                ) {
                    stackedItem[key] = item[key];
                }
            });

            // Always include Total in the data for tooltip display, but don't render it as a bar
            if (item.Total !== undefined) {
                stackedItem.Total = item.Total;
            }

            if (item.errors !== undefined && props?.renderError) {
                stackedItem.errors = item.errors || 0;
            }

            return stackedItem;
        });
    };

    // Check if data is empty
    const isEmpty =
        !loader &&
        (!renderData ||
            renderData.length === 0 ||
            renderData.every((item) =>
                Object.values(item).every(
                    (value) =>
                        value === null ||
                        value === undefined ||
                        value === 0 ||
                        value === "",
                ),
            ));

    // Empty state component
    const EmptyState = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
                minHeight: "300px",
                padding: "40px 20px",
                textAlign: "center",
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                border: "1px solid #e0e0e0",
            }}
        >
            <Box
                sx={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                }}
            >
                <BarChartIcon sx={{ fontSize: 40, color: "#999" }} />
            </Box>
            <Typography
                variant="h6"
                sx={{
                    color: "#666",
                    fontWeight: 500,
                    marginBottom: "8px",
                }}
            >
                No Data Available
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: "#999",
                    maxWidth: "300px",
                    lineHeight: 1.5,
                }}
            >
                There's no data to display in the chart. Data will appear here
                once leads are generated.
            </Typography>
        </Box>
    );

    return (
        <div style={{ width: "100%", height: 400, position: "relative" }}>
            {loader && (
                <div className="chart-loader">
                    <ClipLoader color="#000000" size={35} />
                </div>
            )}
            {isEmpty ? (
                <EmptyState />
            ) : (
                <ResponsiveContainer>
                    <BarChart
                        width={850}
                        height={350}
                        data={createStackedData()}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis
                            yAxisId="left"
                            allowDecimals={false}
                            domain={["auto", "auto"]}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={false} />
                        <Legend content={renderLegend} />

                        {props.selectedStatus === "success"
                            ? barKeys
                                  ?.filter((key) => key !== "errors")
                                  ?.map((item, index) => {
                                      const colorIndex = index;
                                      return (
                                          <Bar
                                              key={item}
                                              dataKey={item}
                                              fill={colors[colorIndex]}
                                              stackId="stack"
                                              yAxisId="left"
                                              activeBar={false}
                                          />
                                      );
                                  })
                            : null}

                        {props?.renderError &&
                            props.selectedStatus === "error" && (
                                <Bar
                                    dataKey="errors"
                                    fill="red"
                                    stackId="stack"
                                    yAxisId="left"
                                    name="All Errors"
                                    activeBar={false}
                                    stroke="darkred"
                                    strokeWidth={2}
                                    radius={[4, 4, 0, 0]}
                                />
                            )}
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default ChartComponent;
